import { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useAuth } from '../../contexts/AuthContext';
import { FirebaseError } from 'firebase/app';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import AcceptInvitation from '../../components/organization/AcceptInvitation';

const Signup = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();
  
  // Extraire l'email de l'URL s'il est présent
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get('email');
    
    if (emailFromUrl) {
      console.log('Email trouvé dans l\'URL:', emailFromUrl);
      setFormData(prev => ({
        ...prev,
        email: emailFromUrl
      }));
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      return setError('Passwords do not match');
    }

    if (formData.password.length < 6) {
      return setError('Password should be at least 6 characters');
    }

    if (!formData.firstName || !formData.lastName) {
      return setError('First name and last name are required');
    }

    try {
      setError('');
      setLoading(true);
      
      // Extraire l'email de l'URL pour le réutiliser après l'inscription
      const params = new URLSearchParams(location.search);
      const emailFromUrl = params.get('email');
      
      console.log('Attempting signup with email:', formData.email);
      
      // Créer l'utilisateur dans Firebase Authentication
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Créer le document utilisateur manuellement sans attendre
      try {
        const now = new Date();
        const timestamp = Timestamp.fromDate(now);
        
        // Document utilisateur principal
        setDoc(doc(db, 'users', firebaseUser.uid), {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email.toLowerCase(),
          createdAt: timestamp,
          updatedAt: timestamp,
        }).catch((err: Error) => console.error("Erreur lors de la création du document utilisateur:", err));

        // Initialiser les préférences de notification sans attendre
        setDoc(doc(db, 'users', firebaseUser.uid, 'settings', 'notifications'), {
          enabled: false,
          maintenance: true,
          fuel: true,
          mileage: true,
          updatedAt: now.toISOString()
        }).catch((err: Error) => console.error("Erreur lors de l'initialisation des préférences:", err));
      } catch (docErr) {
        console.error("Erreur lors de la création des documents:", docErr);
        // Continuer malgré l'erreur, l'utilisateur est déjà créé dans Authentication
      }
      
      console.log('Signup successful!');
      
      // Rediriger vers la page d'accueil
      navigate('/');
      
    } catch (err) {
      console.error('Signup error:', err);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            setError('This email is already registered');
            break;
          case 'auth/invalid-email':
            setError('Invalid email address');
            break;
          case 'auth/operation-not-allowed':
            setError('Email/password accounts are not enabled. Please contact support.');
            break;
          case 'auth/weak-password':
            setError('Password is too weak');
            break;
          default:
            setError(err.message);
        }
      } else {
        setError('Failed to create an account. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleInvitationClose = () => {
    setShowInvitationDialog(false);
    navigate('/');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: 1,
            p: 4
          }}
        >
          <Box
            component="img"
            src="/logo-verifleet1.png"
            alt="VeriFleet Logo"
            sx={{
              width: '280px',
              height: 'auto',
              mb: 4
            }}
          />
          <Box sx={{ typography: 'h5', component: 'h1', mb: 2 }}>
            Sign up
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="firstName"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  autoFocus
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!formData.firstName}
                  helperText={!formData.firstName ? 'First name is required' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!formData.lastName}
                  helperText={!formData.lastName ? 'Last name is required' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={Boolean(error && error.includes('email'))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  error={Boolean(error && error.includes('password'))}
                  helperText="Password must be at least 6 characters"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(formData.confirmPassword && formData.password !== formData.confirmPassword)}
                  helperText={
                    formData.confirmPassword && formData.password !== formData.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing up...' : 'Sign Up'}
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Invitation Dialog */}
        <AcceptInvitation
          open={showInvitationDialog}
          onClose={handleInvitationClose}
          email={formData.email}
        />
      </Container>
    </Box>
  );
};

export default Signup;
